import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { firebaseAuthService } from '@/shared/services/auth/firebaseAuthService';
import { userProfileSyncService } from '@/shared/services/userProfileSyncService';

interface PublicRouteProps {
  children: React.ReactNode;
}

/**
 * Public route wrapper
 * Redirects authenticated users away from auth/signup pages to their appropriate dashboard
 * or to the page they were trying to access before being redirected to auth
 */
const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    // Listen to auth state changes
    const unsubscribe = firebaseAuthService.onAuthStateChanged(async (user) => {
      if (user) {
        setIsAuthenticated(true);
        // Quick role check for redirect
        try {
          const supabaseProfile = await userProfileSyncService.getOrCreateSupabaseProfile(user);
          const role = supabaseProfile.role === 'admin' ? 'admin' : 'client';
          setUserRole(role);
        } catch (profileError) {
          console.warn('Warning: Could not fetch user profile, defaulting to client:', profileError);
          setUserRole('client');
        }
      } else {
        setIsAuthenticated(false);
        setUserRole(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // If authenticated and on auth/signup pages, redirect to appropriate destination
  if (isAuthenticated && userRole && (location.pathname === '/auth' || location.pathname === '/signup')) {
    // Check if there's a saved destination from sessionStorage
    const savedDestination = sessionStorage.getItem('intendedDestination');

    if (savedDestination) {
      // Clear the saved destination
      sessionStorage.removeItem('intendedDestination');

      // Validate that the saved path is appropriate for this user's role
      const isAdminPath = savedDestination.startsWith('/admin/');
      const isClientPath = savedDestination.startsWith('/client/');

      if ((userRole === 'admin' && isAdminPath) || (userRole === 'client' && isClientPath)) {
        return <Navigate to={savedDestination} replace />;
      }
    }

    // Fall back to default dashboard
    return <Navigate to={userRole === 'admin' ? '/admin/dashboard' : '/client/dashboard'} replace />;
  }

  return <>{children}</>;
};

export default PublicRoute;
